// Shell-level TrialBanner (US-191).
//
// DS Wave D shipped the `TrialBanner` composite (full + compact). This file
// is the **app-shell wiring**: it derives the `daysLeft` integer from the
// subscription state via the api-client's `useBilling()` hook, picks the
// matching translation slot, and wires Cancel / Upgrade callbacks to the
// router (a `/settings/billing` deep-link).
//
// State taxonomy (per `plans/03-architecture/billing.md` §Trial state machine):
//   - normal       — daysLeft ≥ 3   (NOT shown — too noisy for the shell row)
//   - urgent       — 1 ≤ daysLeft ≤ 2 (orange banner; global app-shell strip)
//   - endingToday  — daysLeft === 0  (red strip, "ends today")
//   - expired      — daysLeft < 0    (red strip, soft-block hint)
//
// Wave B6 — gate the shell strip to urgent/endingToday/expired only (so a
// trialing user with 6 days left doesn't see a permanent banner). Per-session
// dismiss via `sessionStorage` so a user can hide the strip until the next
// login; if the state is still urgent on the next session it re-shows.
//
// `useBilling()` returns `isError=true` until the route lands, so we
// short-circuit on `isError` to keep the banner hidden in legacy callsites.

import { useEffect, useState, type ReactNode } from 'react';

import { useBilling, type BillingStatus } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { TrialBanner as DsTrialBanner, deriveTrialBannerState } from '@barel/ui';

const DISMISS_KEY = 'barel.trial_banner_dismissed_v1';

function readDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.sessionStorage.setItem(DISMISS_KEY, '1');
    else window.sessionStorage.removeItem(DISMISS_KEY);
  } catch {
    /* private mode / quota — fine to swallow */
  }
}

export interface ShellTrialBannerProps {
  /** Override `daysLeft` for stories / E2E fixtures. */
  daysLeftOverride?: number;
  /** Override `status` for stories / E2E fixtures. */
  statusOverride?: BillingStatus;
  /** Cancel-trial click handler. Defaults to a no-op until EPIC-15 ships. */
  onCancel?: () => void;
  /** Upgrade-trial click handler. Defaults to a no-op until EPIC-15 ships. */
  onUpgrade?: () => void;
}

/**
 * Shell-level TrialBanner — reads subscription state via `useBilling()` and
 * delegates to the DS composite. Returns `null` when the user is not in a
 * trialing state, when the billing API is unavailable, when the daysLeft is
 * still in the "normal" zone (≥3), or when the user has dismissed the
 * banner this session.
 */
export function TrialBanner({
  daysLeftOverride,
  statusOverride,
  onCancel,
  onUpgrade,
}: ShellTrialBannerProps): ReactNode {
  const t = useT('shell');
  const billing = useBilling();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  // Re-sync from sessionStorage on mount (other tabs / SSR boot).
  useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  const status = statusOverride ?? billing.data?.trial.status;
  const trialDaysLeft = daysLeftOverride ?? billing.data?.trial.trialDaysLeft ?? undefined;

  // Hide the banner unless we have an explicit `daysLeft` value AND the
  // status is one we know how to render. Wave-8 stub returns `isError=true`
  // when called → short-circuit.
  if (daysLeftOverride === undefined && (billing.isError || trialDaysLeft === undefined)) {
    return null;
  }

  const daysLeft = trialDaysLeft ?? 0;
  const trialing = status === 'trialing' || daysLeftOverride !== undefined;
  const expired =
    status === 'past_due' || status === 'unpaid' || status === 'canceled' || daysLeft < 0;

  if (!trialing && !expired) return null;

  const derivedState = deriveTrialBannerState(daysLeft);
  // Gate (US-191 Wave B6): only show when the situation is actually urgent.
  // `normal` (≥3 days) stays in the settings-internal `<SettingsTrialBanner />`.
  if (derivedState === 'normal') return null;

  // Per-session dismiss (refreshes when sessionStorage clears).
  if (dismissed) return null;

  const upgradeLabel =
    derivedState === 'expired'
      ? t('trialBanner.upgradeLabelExpired')
      : t('trialBanner.upgradeLabel');

  return (
    <div data-region="shell-trial-banner">
      <DsTrialBanner
        daysLeft={daysLeft}
        title={t('trialBanner.title')}
        label={t('trialBanner.labelNormal', { daysLeft })}
        labelUrgent={t('trialBanner.labelUrgent', { daysLeft })}
        labelEndingToday={t('trialBanner.labelEndingToday')}
        labelExpired={t('trialBanner.labelExpired')}
        cancelLabel={t('trialBanner.cancelLabel')}
        upgradeLabel={upgradeLabel}
        onCancel={() => {
          // Treat the in-banner cancel as a per-session dismiss, then forward
          // to the consumer-supplied callback if present.
          setDismissed(true);
          writeDismissed(true);
          onCancel?.();
        }}
        onUpgrade={onUpgrade ?? (() => undefined)}
      />
    </div>
  );
}
